import { Button, ContentSectionContainer, DialogHandler, Dropdown, EStorageType, fetchRoute, FullBar, GenericWrapperContext, Header, Icon, ListTemplateModel, Search, setStorage, TDataTableRowData, TemplatesFactory, TGenericListReturn, TOrder } from "cakemail-ui-components-v2"
import { MouseEvent, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TemplatesContainerContext } from "./context"
import "./styles.scss"
import { TTemplatesFilters, TTemplatesStoredFilters } from "./types"
import { useNavigate } from "react-router-dom"
import { SelectChangeEvent } from "@mui/material/Select"
import { getCurrentStoredFilters, setCurrentStoredFilters } from "../../utils/components"
import { ERoutes } from "../../utils/url"
import { TemplatesTable } from "../../components/templates/tableListing"


export default function TemplatesListing({ ...props }) {
    const filterStorageKey = "templatesTableFilters"
    const genericWrapper = useContext(GenericWrapperContext)
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    const initFilters: TTemplatesFilters = getCurrentStoredFilters({ key: filterStorageKey, accountId: String(genericWrapper.account?.id) })

    const [order, setOrder] = useState<TOrder>(initFilters?.order || "asc")
    const [orderBy, setOrderBy] = useState<string>(initFilters?.orderBy || "name")
    const [page, setPage] = useState<number>(1)
    const [search, setSearch] = useState<string>(initFilters?.search || "")
    const [loading, setLoading] = useState<boolean>(true)
    const [tag, setTag] = useState<string>(initFilters?.tag || "all")
    const [openedSearch, setOpenedSearch] = useState(!!initFilters?.search)
    const [ownerFilter, setOwnerFilter] = useState(initFilters?.ownerFilter || "is_owner==true")

    const [templates, setTemplates] = useState<TGenericListReturn<ListTemplateModel>>()

    useEffect(fetchTemplates, [order, orderBy, page, tag, search, ownerFilter])
    useEffect(handleSearchFocus, [openedSearch])

    function fetchTemplates() {
        setLoading(true)
        const filters = []

        if (tag && tag !== "all") filters.push(`tag==${tag}`)
        if (search) filters.push(`name==*${search}*`)

        filters.push(ownerFilter)

        TemplatesFactory.list({
            withCount: true,
            sort: orderBy ? order === "asc" ? "+" + orderBy : "-" + orderBy : orderBy,
            page: page,
            perPage: 50,
            filter: filters.join(";")
        })
            .then((data: TGenericListReturn<ListTemplateModel>) => {
                setCurrentStoredFilters<TTemplatesFilters>({
                    key: filterStorageKey,
                    accountId: Number(genericWrapper.account?.id),
                    data: {
                        search: search,
                        tag: tag,
                        order: order,
                        orderBy: orderBy,
                        ownerFilter: ownerFilter
                    }
                })

                setLoading(false)
                setTemplates(data)
            })
            .catch(({ msg }) => {
                setLoading(false)
                DialogHandler.show({
                    title: t(msg || "error-templates-fetch"),
                    type: "error"
                })
            })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".clientSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function createTemplate() {
        navigate(fetchRoute({ route: ERoutes.templateDetails, params: { templateId: "new" } }))
    }

    function handleChangeTagFilter(event: MouseEvent<HTMLButtonElement>) {
        const { name } = event.currentTarget
        setPage(1)
        setTag(name)
    }

    function handleOwnerFilterDD(event: SelectChangeEvent<string>) {
        const { name, value } = event.target
        setOwnerFilter(value)
    }

    return <ContentSectionContainer className="manageTemplateAssets">
        <TemplatesContainerContext.Provider value={{
            templates: templates,
            setTemplates
        }}
        >
            <FullBar
                left={
                    <Header
                        support={t("assets-templates-listing-subtitle-txt")}
                        heading={t("general-email-template_other")}
                        headingVariant="h3L"
                        headingTooltip={t("assets-templates-listing-heading-tooltip")}
                    />
                }
                right={<>
                    <Button color="secondary" onClick={createTemplate}><Icon name="Add18" /></Button>
                    <Button variant="outlined" color="body1" onClick={handleSearchOpen}><Icon name="Search18" /></Button>
                    <Dropdown
                        onChange={handleOwnerFilterDD}
                        value={ownerFilter}
                        options={[
                            {
                                text: t("general-all-template"),
                                value: "is_not_owner==true;is_owner==true",
                            },
                            {
                                text: t("general-my-templates"),
                                value: "is_owner==true",
                            },
                            {
                                text: t("general-general-templates"),
                                value: "is_not_owner==true;is_owner==false",
                            },
                        ]}
                    />
                </>}
            />

            {openedSearch &&
                <Search placeholder={t("assets-templates-listing-search-placeholder")} initValue={search} className="clientSearch" onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
            }

            <TemplatesTable
                reloadTable={fetchTemplates}
                templates={templates}
                orderBy={orderBy}
                loading={loading}
                handleChangeTagFilter={handleChangeTagFilter}
                handlePaginationChange={handlePaginationChange}
                handleSortChange={handleSortChange}
                tag={tag}
            />
        </TemplatesContainerContext.Provider>
    </ContentSectionContainer>
}